import type { AuthenticationConfig } from '../config/authentication';

/**
 * Journey Configuration Types
 * Defines the configuration schema for customer portal journey experience
 * (UI, branding, payment options - NOT communication timing/AI rules)
 */

export interface JourneyConfiguration {
  payment: PaymentConfiguration;
  ui: UIConfiguration;
  authentication: AuthenticationConfig;
}

export interface PaymentMethodProviderConfig {
  enabled: boolean;
}

export interface PaymentMethodProviders {
  card: PaymentMethodProviderConfig;
  sepa: PaymentMethodProviderConfig;
  multibanco: PaymentMethodProviderConfig;
  mb_way: PaymentMethodProviderConfig;
  bacs: PaymentMethodProviderConfig;
}

export interface PaymentConfiguration {
  allowPartialPayment: boolean; // If false, hide ALL partial payment functionality (tabs + edit button)
  partialPaymentTab: boolean; // If true, show as separate tab; if false, show as edit button in "Pay in Full" tab
  allowAccountPartialPayment: boolean; // Account-level (pay all) partial tab. Off by default; independent of case-level partials
  allowFullPayLater: boolean; // Allow future date selection for full payment
  allowPartialPayLater: boolean; // Allow future date selection for partial payment (if false, locks to today)
  enablePaymentPlan: boolean; // Show payment plan tab
  planPaymentMethods: string[]; // Payment methods available for payment plans
  providers: PaymentMethodProviders;
  paymentPlan?: PaymentPlanConfiguration;
  maxDaysInFuture: number; // Maximum days in the future a payment can be scheduled
  minDebtForPartialPayment: number; // Minimum debt amount required to enable partial payments
  minPartialPaymentAmount: number; // Minimum amount allowed for a single partial payment (0 = no minimum)
}

/**
 * Payment Plan Tier
 * Defines payment plan rules for a specific debt threshold range
 *
 * Min monthly payment is calculated as: debtThreshold ÷ maxDurationMonths
 */
export interface PaymentPlanTier {
  id: string; // Unique identifier (UUID)
  debtThreshold: number; // Starting amount for this tier (€)
  maxDurationMonths: number; // Maximum plan length (2-24 months)
  minInstallmentAmount?: number; // Minimum payment per installment (€)
  label?: string; // Optional description (e.g., "Small debts")
}

/**
 * Payment Plan Configuration
 * Supports both legacy (single tier) and new (multi-tier) formats
 */
export type PaymentFrequency = 'monthly' | 'bi-weekly' | 'weekly';

export interface PaymentPlanConfiguration {
  // New tier-based format
  minEligibleDebt?: number; // Debts below this don't get plans
  tiers?: PaymentPlanTier[]; // Must have at least one tier
  paymentFrequencies?: PaymentFrequency[]; // Available payment frequencies (defaults to ['monthly'])

  // Legacy format (backward compatibility - deprecated)
  maxDurationMonths?: number; // Deprecated: use tiers instead
}

/**
 * Company Information
 * Contains contact details, support info, and legal URLs from content config
 */
export interface CompanyInfo {
  companyName?: string; // Company name
  companyPhone?: string; // Company phone number
  companyEmail?: string; // Company email address
  companyAddress?: string; // Full company address
  fcaFrn?: string; // FCA firm reference number (UK)
  supportUrl?: string; // Support/help page URL
  supportEmail?: string; // Support email address
  supportPhone?: string; // Support phone number
  supportAddress?: string; // Support mailing address
  termsUrl?: string; // Terms and conditions URL
  privacyPolicyUrl?: string; // Privacy policy URL
  unsubscribeUrlTemplate?: string; // Unsubscribe URL template with {{token}} placeholder
}

export interface UIConfiguration {
  companyName?: string; // Company name for page title (legacy - use companyInfo.companyName)
  companyInfo?: CompanyInfo; // Full company information from content config
  branding: {
    theme: 'professional' | 'approachable'; // Visual theme style
    logo?: {
      url?: string; // Logo image URL (single logo)
      light?: string; // Logo for light mode
      dark?: string; // Logo for dark mode
      alt?: string; // Alt text for logo
    };
    logoIcon?: {
      light?: string; // Icon logo for light mode
      dark?: string; // Icon logo for dark mode
    }; // Optional icon-only logo
    showGradientSphere?: boolean; // Show decorative gradient sphere (default: true for 'approachable', false for 'professional')
    favicon?: {
      url: string; // Favicon URL (must be .ico or .png)
    };
    colors: {
      primary: string; // Primary brand color (hex)
      primaryForeground?: string; // Text color on primary background (optional, auto-calculated if not provided)
      buttonBackground?: {
        light?: string; // Button background for light mode
        dark?: string; // Button background for dark mode
      };
      buttonText?: {
        light?: string; // Button text for light mode
        dark?: string; // Button text for dark mode
      };
    };
  };
  language: string; // Default language (ISO language code)
  availableLanguages?: string[]; // Available languages for language selector
  features: {
    enableChat?: boolean; // Enable chat functionality
    showMergeAccounts: boolean;
    showPaymentHistory: boolean;
    showDisputes: boolean;
    showDocuments?: boolean; // Show documents section
    showAccountBalance?: boolean; // Show account balance
  };
  termsLinks?: Array<{
    name: string | Record<string, string>; // Display name - can be simple string or localized object keyed by BCP47 locale (e.g., {"en-GB": "Secure", "pt-PT": "Seguro"})
    url: string; // URL to terms page
  }>;
}

/**
 * Default configuration values
 * Used as fallback when no client-specific config exists
 */
export const DEFAULT_JOURNEY_CONFIG: JourneyConfiguration = {
  payment: {
    allowPartialPayment: true,
    partialPaymentTab: true,
    allowAccountPartialPayment: false,
    allowFullPayLater: true,
    allowPartialPayLater: true,
    enablePaymentPlan: true,
    planPaymentMethods: ['card'],
    providers: {
      card: { enabled: true },
      sepa: { enabled: true },
      multibanco: { enabled: true },
      mb_way: { enabled: true },
      bacs: { enabled: false },
    },
    maxDaysInFuture: 30,
    minDebtForPartialPayment: 10,
    minPartialPaymentAmount: 0,
    paymentPlan: {
      minEligibleDebt: 0,
      tiers: [
        {
          id: 'tier_default',
          debtThreshold: 0,
          maxDurationMonths: 12,
        },
      ],
      paymentFrequencies: ['monthly'],
    },
  },
  ui: {
    branding: {
      theme: 'professional',
      colors: {
        primary: '#7f53dd',
      },
    },
    language: 'en-GB',
    availableLanguages: ['en-GB'],
    features: {
      enableChat: false,
      showMergeAccounts: true,
      showPaymentHistory: true,
      showDisputes: true,
      showDocuments: true,
      showAccountBalance: true,
    },
    termsLinks: [],
  },
  authentication: {
    authMode: 'none',
    allowGuestCheckout: false,
    twoFactorMethods: ['email', 'sms'],
    restrictedPages: ['/account'],
    requireVerificationForPayments: true,
    requireVerificationForPlans: true,
  },
};
